import { useEffect, useState } from 'react';
import { Globe, Info, X, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

/**
 * Fase 1 av engelsk-tekst-visning (task fra 2026-07-01):
 *
 * En knapp øverst i studieplanen som lar brukeren SE gjeldende engelsk
 * oversettelse uten å måtte gå til TranslationEditor-flyten (som er
 * designet for redigering, ikke visning).
 *
 * Formål:
 * - Gi transparens om HVA som ligger som engelsk versjon
 * - Forklare HVORFOR tospråklige attester kreves (EU Micro-credentials +
 *   Europass-standard)
 * - Sette forventninger om at KI-oversettelse er kvalitetssikret men ikke
 *   nødvendigvis ordrett — hvis brukeren opplever unøyaktige ord, skyldes
 *   det ofte at norsk/engelsk fagterminologi ikke har 1:1-samsvar. Da bør
 *   den norske teksten presiseres, ikke bare den engelske.
 *
 * Fase 2 og 3 (regenerering ved endring, public verify-side) står i pending-
 * listen "Til vurdering".
 */

interface EnglishPreviewButtonProps {
  studyPlanId: string;
}

interface TranslationData {
  title_en: string;
  description_en: string;
  target_group_en: string;
  admission_requirements_en: string;
  teaching_learning_methods_en: string;
  assessment_criteria_en: string;
  learning_outcomes_en: {
    knowledge?: string[];
    skills?: string[];
    competence?: string[];
  };
  content_modules_en: Array<{ title: string; description?: string }>;
  status: string;
  approved: boolean;
  approved_at: string | null;
  generated_at: string | null;
}

export default function EnglishPreviewButton({ studyPlanId }: EnglishPreviewButtonProps) {
  const [hasTranslation, setHasTranslation] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [translation, setTranslation] = useState<TranslationData | null>(null);
  const [loading, setLoading] = useState(false);

  // Sjekk stille om det finnes en oversettelse (bare da vises knappen)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/translation`);
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        // Kun vis knappen hvis oversettelsen faktisk har innhold
        if (data.success && data.translation && data.translation.title_en) {
          setHasTranslation(true);
          setTranslation(data.translation);
        }
      } catch {
        // stille — hvis endepunktet ikke er tilgjengelig, skjuler vi knappen
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [studyPlanId]);

  // Last inn ferskt hver gang modalen åpnes
  const openModal = async () => {
    setShowModal(true);
    setLoading(true);
    try {
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/translation`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) setTranslation(data.translation);
      }
    } finally {
      setLoading(false);
    }
  };

  if (checking || !hasTranslation) return null;

  const isApproved = translation?.approved || translation?.status === 'approved';

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-kf-primary bg-kf-primary-10 border border-kf-primary-40 rounded-md hover:bg-kf-primary-20 transition-colors"
        title="Se engelsk versjon av studieplanen (brukes til internasjonale attester)"
      >
        <Globe size={16} />
        Vis engelsk versjon
        {isApproved ? (
          <CheckCircle2 size={14} className="text-green-600" />
        ) : (
          <AlertTriangle size={14} className="text-amber-500" />
        )}
      </button>

      {showModal && (
        <div
          className="fixed inset-0 z-40 flex items-start justify-end bg-black/40"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white shadow-2xl w-full max-w-2xl h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Globe className="text-kf-primary" size={20} />
                <h2 className="text-lg font-bold text-kf-dark">Engelsk versjon</h2>
                {isApproved ? (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded">
                    <CheckCircle2 size={12} /> Godkjent
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded">
                    <AlertTriangle size={12} /> Utkast — ikke godkjent
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-1 rounded hover:bg-gray-100"
                aria-label="Lukk"
              >
                <X size={20} />
              </button>
            </div>

            {/* Forklaringsboks — dette er kjernebudskapet */}
            <div className="p-4 border-b border-gray-100 bg-blue-50">
              <div className="flex items-start gap-2 text-sm">
                <Info size={16} className="flex-shrink-0 mt-0.5 text-blue-600" />
                <div className="text-blue-900 space-y-2">
                  <p>
                    <strong>Om denne oversettelsen.</strong>{' '}
                    Alle kompetanseattester utstedes tospråklig (norsk + engelsk) for at
                    kandidaten skal kunne bruke dem i internasjonale sammenhenger —
                    jobbsøknad, videre utdanning eller anerkjennelse i utlandet. Dette følger
                    EU Micro-credentials Framework og Europass-standarden.
                  </p>
                  <p>
                    KI-en oversetter tittel, læringsutbytter og innhold. Kvaliteten
                    sammenlignes automatisk med norsk kildetekst under KI-validering.
                  </p>
                  <p>
                    Hvis du opplever at oversettelsen bruker ord som ikke helt matcher det du
                    hadde tenkt, skyldes det ofte at norsk og engelsk fagterminologi ikke har
                    1:1-samsvar. <strong>Anbefaling:</strong> vurder å presisere den norske
                    teksten (bruk mer entydige fagord), lagre, og kjør KI-validering på nytt.
                    Den engelske versjonen regenereres da basert på den nye norske teksten.
                  </p>
                  <p className="text-xs text-blue-800">
                    Redigering direkte i engelsk gjøres i «Rediger oversettelse»-seksjonen
                    lenger ned på siden (kun tilgjengelig for godkjente studieplaner).
                  </p>
                </div>
              </div>
            </div>

            {/* Innhold — read-only */}
            {loading ? (
              <div className="p-8 flex items-center justify-center text-gray-500">
                <Loader2 className="animate-spin mr-2" size={20} />
                Laster oversettelse…
              </div>
            ) : translation ? (
              <div className="p-4 space-y-5 text-sm">
                <Section title="Title">{translation.title_en || <em className="text-gray-400">— mangler —</em>}</Section>
                <Section title="Description">{translation.description_en || <em className="text-gray-400">— mangler —</em>}</Section>

                {translation.target_group_en && (
                  <Section title="Target group">{translation.target_group_en}</Section>
                )}
                {translation.admission_requirements_en && (
                  <Section title="Admission requirements">{translation.admission_requirements_en}</Section>
                )}
                {translation.teaching_learning_methods_en && (
                  <Section title="Teaching and learning methods">{translation.teaching_learning_methods_en}</Section>
                )}

                {translation.learning_outcomes_en && (
                  <div>
                    <h3 className="font-semibold text-kf-dark mb-2">Learning outcomes</h3>
                    <OutcomeList label="Knowledge" items={translation.learning_outcomes_en.knowledge} />
                    <OutcomeList label="Skills" items={translation.learning_outcomes_en.skills} />
                    <OutcomeList label="Competence" items={translation.learning_outcomes_en.competence} />
                  </div>
                )}

                {translation.content_modules_en && translation.content_modules_en.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-kf-dark mb-2">Content modules</h3>
                    <ol className="space-y-2 list-decimal list-inside">
                      {translation.content_modules_en.map((m, i) => (
                        <li key={i} className="ml-2">
                          <span className="font-medium">{m.title}</span>
                          {m.description && (
                            <p className="text-gray-600 mt-1 ml-6">{m.description}</p>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {translation.assessment_criteria_en && (
                  <Section title="Assessment criteria">{translation.assessment_criteria_en}</Section>
                )}

                {/* Timestamps */}
                <div className="border-t border-gray-100 pt-3 text-xs text-gray-500 space-y-1">
                  {translation.generated_at && (
                    <div>KI-generert: {new Date(translation.generated_at).toLocaleString('nb-NO')}</div>
                  )}
                  {translation.approved_at && (
                    <div>Godkjent: {new Date(translation.approved_at).toLocaleString('nb-NO')}</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-8 text-gray-500 text-sm">Ingen oversettelse funnet.</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="font-semibold text-kf-dark mb-1">{title}</h3>
      <p className="text-gray-700 whitespace-pre-wrap">{children}</p>
    </div>
  );
}

function OutcomeList({ label, items }: { label: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-2">
      <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</h4>
      <ul className="list-disc list-inside space-y-1 ml-2">
        {items.map((it, i) => (
          <li key={i} className="text-gray-700">{it}</li>
        ))}
      </ul>
    </div>
  );
}
